import classNames from 'classnames';
import { memo, type MouseEvent as ReactMouseEvent, type ReactNode, useCallback, useMemo } from 'react';

import { DEFAULT_THUMBNAIL_DIMENSIONS } from '~/components/TableView/NameCell/dimensions';
import { TableCell } from '~/components/TableView/TableCell';
import { useTableView } from '~/components/TableView/TableViewContext';
import { makeActiveTableColumnFromOption } from '~/components/TableView/tableColumnWidthsUtils';

export type NameCellContentProps = {
  disabled?: boolean;
  title: ReactNode;
  children?: ReactNode;
  image: ReactNode;
  imageSize?: number;
  'data-testid'?: string;
};

/**
 * This component is used to display data in name cell
 */
export const NameCellContent = memo(
  ({ disabled, image, title, children, 'data-testid': dataTestId }: NameCellContentProps) => {
    const { getColumnWidthStyle } = useTableView();
    const handleMouseEvent = useCallback((e: ReactMouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    }, []);

    const optionColumn = useMemo(() => makeActiveTableColumnFromOption('title', false, undefined), []);

    return (
      <TableCell style={getColumnWidthStyle(optionColumn)}>
        <div
          className={classNames('flex items-center', disabled ? 'pointer-events-none' : 'pointer-events-auto')}
          data-testid={dataTestId}
        >
          <div
            className="relative"
            style={{ ...DEFAULT_THUMBNAIL_DIMENSIONS, minWidth: DEFAULT_THUMBNAIL_DIMENSIONS.width }}
          >
            <div
              className="relative flex items-center justify-center overflow-hidden rounded-2 p-0 [&>*]:h-full [&>*]:grow [&>img]:w-full [&>img]:object-cover"
              style={{ ...DEFAULT_THUMBNAIL_DIMENSIONS }}
            >
              {image}
            </div>
          </div>
          {title}
          {!!children && (
            <>
              {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: the handlers only stop propagation so menu clicks don't reach the row — there is no keyboard action to add. */}
              <div
                className="ml-auto flex shrink-0"
                // Prevent the Table View row hover states from continually being updated
                // while the user moves their mouse cursor up and down the context menu options
                onClick={handleMouseEvent}
                onDoubleClick={handleMouseEvent}
                onMouseMove={handleMouseEvent}
              >
                {children}
              </div>
            </>
          )}
        </div>
      </TableCell>
    );
  },
);

NameCellContent.displayName = 'NameCellContent';
